import { SERVICE_URL } from "./app.const";
import { fetchDelete, fetchGet, fetchPost, fetchPut } from "./response";

const normalizeQuest = (quest) => ({
  assignedCharacterIds: Array.isArray(quest?.assignedCharacterIds)
    ? quest.assignedCharacterIds
    : [],
  createdAt: quest?.createdAt ?? "",
  description: quest?.description ?? "",
  id: quest?.id ?? "",
  notes: quest?.notes ?? "",
  status: quest?.status ?? "new",
  title: quest?.title ?? "",
  updatedAt: quest?.updatedAt ?? "",
});

const normalizeEncounterEnemy = (enemy) => ({
  count: Number.isFinite(enemy?.quantity)
    ? enemy.quantity
    : Number.isFinite(enemy?.count)
      ? enemy.count
      : 1,
  challengeRating: enemy?.challengeRating ?? "",
  currentHp: Number.isFinite(enemy?.currentHp) ? enemy.currentHp : 0,
  id: enemy?.id ?? "",
  initiative: Number.isFinite(enemy?.initiative) ? enemy.initiative : null,
  maxHp: Number.isFinite(enemy?.maxHp) ? enemy.maxHp : 0,
  name: enemy?.name ?? "",
  notes: enemy?.notes ?? "",
});

const normalizeEncounter = (encounter) => ({
  createdAt: encounter?.createdAt ?? "",
  description: encounter?.description ?? "",
  enemyEntries: Array.isArray(encounter?.enemyEntries)
    ? encounter.enemyEntries.map(normalizeEncounterEnemy)
    : [],
  id: encounter?.id ?? "",
  experienceReward: Number.isFinite(encounter?.experienceReward) ? encounter.experienceReward : 0,
  notes: encounter?.notes ?? "",
  rewardItems: Array.isArray(encounter?.rewardItems) ? encounter.rewardItems : [],
  title: encounter?.title ?? "",
  updatedAt: encounter?.updatedAt ?? "",
});

export const normalizeCampaign = (campaign) => ({
  createdAt: campaign?.createdAt ?? "",
  description: campaign?.description ?? "",
  encounters: Array.isArray(campaign?.encounters)
    ? campaign.encounters.map(normalizeEncounter)
    : [],
  id: campaign?.id ?? "",
  masterUserId: campaign?.masterUserId ?? "",
  memberCharacterIds: Array.isArray(campaign?.memberCharacterIds)
    ? campaign.memberCharacterIds
    : [],
  name: campaign?.name ?? "",
  questIds: Array.isArray(campaign?.questIds) ? campaign.questIds : [],
  quests: Array.isArray(campaign?.quests) ? campaign.quests.map(normalizeQuest) : [],
  updatedAt: campaign?.updatedAt ?? "",
});

const unwrapCampaignList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  return [];
};

export async function fetchCampaigns(token) {
  const response = await fetchGet(`${SERVICE_URL}/campaigns`, {}, token);
  if (response.isError) return response;

  response.data = unwrapCampaignList(response.data).map(normalizeCampaign);
  return response;
}

export async function fetchCampaignById(campaignId, token) {
  const response = await fetchGet(`${SERVICE_URL}/campaigns/${campaignId}`, {}, token);
  if (response.isError || !response.data) return response;

  response.data = normalizeCampaign(response.data);
  return response;
}

export async function fetchAvailableCampaignCharacters(campaignId, token) {
  return fetchGet(
    `${SERVICE_URL}/campaigns/${campaignId}/available-characters`,
    { size: 500 },
    token
  );
}

export async function createCampaign(payload, token) {
  const response = await fetchPost(`${SERVICE_URL}/campaigns`, token, {
    headers: { "Content-Type": "application/json" },
    body: {
      description: payload.description.trim(),
      name: payload.name.trim(),
    },
  });

  if (response.isError || !response.data) return response;
  response.data = normalizeCampaign(response.data);
  return response;
}

export async function updateCampaign(campaignId, payload, token) {
  const response = await fetchPut(`${SERVICE_URL}/campaigns/${campaignId}`, token, {
    body: {
      description: payload.description.trim(),
      name: payload.name.trim(),
    },
  });

  if (response.isError || !response.data) return response;
  response.data = normalizeCampaign(response.data);
  return response;
}

export function deleteCampaign(campaignId, token) {
  return fetchDelete(`${SERVICE_URL}/campaigns/${campaignId}`, token);
}

export function addCharacterToCampaign(campaignId, characterId, token) {
  return fetchPost(`${SERVICE_URL}/campaigns/${campaignId}/characters`, token, {
    headers: { "Content-Type": "application/json" },
    body: { characterId },
  });
}

export function removeCharacterFromCampaign(campaignId, characterId, token) {
  return fetchDelete(`${SERVICE_URL}/campaigns/${campaignId}/characters/${characterId}`, token);
}

export function createCampaignQuest(campaignId, payload, token) {
  return fetchPost(`${SERVICE_URL}/campaigns/${campaignId}/quests`, token, {
    headers: { "Content-Type": "application/json" },
    body: {
      assignedCharacterIds: payload.assignedCharacterIds,
      description: payload.description.trim(),
      notes: payload.notes.trim(),
      status: payload.status,
      title: payload.title.trim(),
    },
  });
}

export function deleteCampaignQuest(campaignId, questId, token) {
  return fetchDelete(`${SERVICE_URL}/campaigns/${campaignId}/quests/${questId}`, token);
}

export function createCampaignEncounter(campaignId, payload, token) {
  return fetchPost(`${SERVICE_URL}/campaigns/${campaignId}/encounters`, token, {
    headers: { "Content-Type": "application/json" },
    body: {
      description: payload.description.trim(),
      enemyEntries: payload.enemyEntries,
      experienceReward: Number(payload.experienceReward) || 0,
      notes: payload.notes.trim(),
      rewardItems: payload.rewardItems ?? [],
      title: payload.title.trim(),
    },
  });
}

export function updateCampaignEncounter(campaignId, encounterId, payload, token) {
  return fetchPut(`${SERVICE_URL}/campaigns/${campaignId}/encounters/${encounterId}`, token, {
    body: {
      description: payload.description.trim(),
      enemyEntries: payload.enemyEntries,
      experienceReward: Number(payload.experienceReward) || 0,
      notes: payload.notes.trim(),
      rewardItems: payload.rewardItems ?? [],
      title: payload.title.trim(),
    },
  });
}

export function deleteCampaignEncounter(campaignId, encounterId, token) {
  return fetchDelete(`${SERVICE_URL}/campaigns/${campaignId}/encounters/${encounterId}`, token);
}
